import { Fragment, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { memoize } from 'lodash-es'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import type { BlockCategories, BlockTemplateType } from '../editorSidebarBlockTemplates'
import { BlockTemplate, createToolDragAndDropPreviewNode } from '../EditorSidebar'
import { DragSource } from '../templateComponents/Workspace/utils/DragAndDrop'
import { Tooltip } from '../Tooltip'
import VideoTutorialButton from '../videoTutorials/VideoTutorialButton'
import blockTemplates from '../editorSidebarBlockTemplates'
import useEventHandler from '../../utils/hooks/useEventHandler'

type BlockCategory = {
  title: BlockCategories
  blocks: BlockTemplateType[]
}

const SIDEBAR_TRANSITION_DURATION = 400 + 100 // (let's add a little buffer)

const getBlockCategories = memoize((isBeyond: boolean): BlockCategory[] => {
  function getVisibleBlocksFor(name: BlockCategories) {
    return blockTemplates.filter(
      (block) => block.categories.includes(name) && !block.hideFor.includes(isBeyond ? 'beyond' : 'unity'),
    )
  }

  return [
    {
      title: 'product',
      blocks: getVisibleBlocksFor('product'),
    },
    {
      title: 'image',
      blocks: getVisibleBlocksFor('image'),
    },
    {
      title: 'text',
      blocks: getVisibleBlocksFor('text'),
    },
    {
      title: 'icon',
      blocks: getVisibleBlocksFor('icon'),
    },
    {
      title: 'gallery',
      blocks: getVisibleBlocksFor('gallery'),
    },
    {
      title: 'video',
      blocks: getVisibleBlocksFor('video'),
    },
    {
      title: 'divider',
      blocks: getVisibleBlocksFor('divider'),
    },
    {
      title: 'expander',
      blocks: getVisibleBlocksFor('expander'),
    },
    {
      title: 'contact',
      blocks: getVisibleBlocksFor('contact'),
    },
    {
      title: 'maps',
      blocks: getVisibleBlocksFor('maps'),
    },
    {
      title: 'html',
      blocks: getVisibleBlocksFor('html'),
    },
  ]
})

type CategoryProps = {
  title: string
  active?: boolean
  onClick: (active: boolean) => void
} & TranslateProps

function Category({ title, onClick, active = false, t }: Readonly<CategoryProps>): ReactElement {
  return (
    <div
      className={cc(['ep-content-element-tab', `ep-content-element-tab-${title}`, { active }])}
      onClick={() => onClick(!active)}
    >
      {t(`contentElementsSection.categories.${title}`)}
    </div>
  )
}

type ContentCreationManagerProps = {
  resetCategory?: boolean
  activePageBlocks: any
  isBeyond: boolean
} & TranslateProps

export function ContentCreationManager({
  resetCategory = false,
  isBeyond,
  activePageBlocks,
  t,
}: Readonly<ContentCreationManagerProps>): ReactElement {
  const [activeCategory, setActiveCategory] = useState<string>('')
  const contentContainerRef = useRef<HTMLDivElement>(null)
  const isTutorialVideoAvailable = useSelector<State, boolean>(
    // Tutorial videos are only available for German merchants at the moment.
    (state) => ['de'].includes(state.getIn(['view', 'interfaceLanguage'])),
  )

  const openSection = (name: string) => {
    setActiveCategory(name)
    setTimeout(() => contentContainerRef.current?.scrollIntoView(), 100)
  }

  useEffect(() => {
    // Unset active category after the sidebar section is fully closed.
    if (resetCategory) setTimeout(() => setActiveCategory(''), 500)
  }, [resetCategory])

  useEffect(followDeepLinks, [])
  useEventHandler('hashchange', followDeepLinks)
  function followDeepLinks() {
    switch (window.location.hash) {
      case '#imageContentElements':
        setTimeout(() => openSection('image'), SIDEBAR_TRANSITION_DURATION)
        break
      case '#textContentElements':
        setTimeout(() => openSection('text'), SIDEBAR_TRANSITION_DURATION)
    }
  }

  const tScoped = (key: string, ...args: any[]): string => t(`contentElementsSection.${key}`, ...args)
  return (
    <div className="ep-sidebar-module-content">
      <div ref={contentContainerRef} className={'ep-scrollable'}>
        <div className="ep-content-element-wrapper">
          {getBlockCategories(isBeyond)
            .filter((category) => Boolean(category.blocks.length))
            .map((category) => (
              <Fragment key={category.title}>
                <Category
                  title={category.title}
                  active={category.title === activeCategory}
                  onClick={(active) => openSection(active ? category.title : '')}
                  t={t}
                />
                {category.title === activeCategory && (
                  <ul className="ep-content-element-list">
                    {category.blocks.map((blockTemplate) => (
                      <Tooltip
                        key={blockTemplate.label}
                        sideOffset={10}
                        hideIcon
                        trigger={
                          <li
                            key={blockTemplate.label}
                            className={cc([
                              { 'ep-tool-disabled': !(blockTemplate.canBeAdded?.(activePageBlocks) ?? true) },
                            ])}
                          >
                            <DragSource
                              component={BlockTemplate}
                              icon={blockTemplate.icon}
                              disabled={!(blockTemplate.canBeAdded?.(activePageBlocks) ?? true)}
                              type="dali-add"
                              onDrag={blockTemplate.generate}
                              previewNodeOffset={{ x: 136 / 2, y: 56 / 1.1 }}
                              createPreviewNode={() => createToolDragAndDropPreviewNode(blockTemplate)}
                            />
                            <span className="ep-tool-label">{tScoped(`elements.${blockTemplate.label}`)}</span>
                          </li>
                        }
                        content={
                          !(blockTemplate.canBeAdded?.(activePageBlocks) ?? true)
                            ? tScoped('blocked')
                            : tScoped('explanation')
                        }
                      />
                    ))}
                  </ul>
                )}
              </Fragment>
            ))}
        </div>
        {isTutorialVideoAvailable && (
          <div className="ep-editor-sidebar-video-tutorial-wrapper">
            <hr className="ep-editor-sidebar-video-tutorial-divider" />
            <VideoTutorialButton tutorial="content">
              {t('contentElementsSection.videoTutorial.videoTutorialButton.label')}
            </VideoTutorialButton>
          </div>
        )}
      </div>
    </div>
  )
}
